import { BASE_API_URL } from '@/constant/api'
import { AppResp } from '@/types/api'
import {
  EngagementMessageListReq,
  EngagementMessageRes,
  ReadAllResponse,
  SystemMessageDetail,
  SystemMessageListReq,
  SystemMessageRes,
  UnreadMessageStatus,
} from '@/types/letter'
import {
  GetCharEventDetailReq,
  GetCharLetterDetailReq,
  GetCharLetterDetailResp,
  GetCharMemoryDetailReq,
  GetCharMemoryDetailResp,
  GetCharMonologueDetailReq,
  GetCharMonologueDetailResp,
  GetCharRomanticMomentDetailReq,
  GetCharRomanticMomentDetailResp,
} from '@/types/relationship'

import { axiosAuth } from '@/utils/axios'

// 获取单个信件详情
export const getCharLetterDetail = async (req: GetCharLetterDetailReq) => {
  const res = await axiosAuth.post<AppResp<GetCharLetterDetailResp>>(
    `${BASE_API_URL}/relationship/reward/letter/get`,
    req
  )

  return res.data.data
}

// 获取单个内心独白详情
export const getCharMonologueDetail = async (
  req: GetCharMonologueDetailReq
) => {
  const res = await axiosAuth.post<AppResp<GetCharMonologueDetailResp>>(
    `${BASE_API_URL}/relationship/reward/monologue/get`,
    req
  )

  return res.data.data
}

// 获取单个专属回忆详情
export const getCharMemoryDetail = async (req: GetCharMemoryDetailReq) => {
  const res = await axiosAuth.post<AppResp<GetCharMemoryDetailResp>>(
    `${BASE_API_URL}/relationship/reward/memory/get`,
    req
  )

  return res.data.data
}

// 获取单个过往事件详情
export const getCharEventDetail = async (req: GetCharEventDetailReq) => {
  const res = await axiosAuth.post<AppResp<{ event?: { content: string } }>>(
    `${BASE_API_URL}/relationship/reward/event/get`,
    req
  )

  return res.data.data
}

// 获取单个梦境详情
export const getCharDreamDetail = async (req: {
  character_id: string
  reward_code: string
  instance_id: string
}) => {
  const res = await axiosAuth.post<AppResp<{ dream?: { content: string } }>>(
    `${BASE_API_URL}/relationship/reward/dream/get`,
    req
  )

  return res.data.data
}

// 获取单个浪漫时刻详情
export const getCharRomanticMomentDetail = async (
  req: GetCharRomanticMomentDetailReq
) => {
  const res = await axiosAuth.post<AppResp<GetCharRomanticMomentDetailResp>>(
    `${BASE_API_URL}/relationship/reward/romantic_moment/get`,
    req
  )

  return res.data.data
}

// 生成心动时刻视频
export const generateRomanticMoment = async (
  req: GetCharRomanticMomentDetailReq
) => {
  const res = await axiosAuth.post<
    AppResp<{
      success: boolean
      message: string
      video_url: string
      status?: number
    }>
  >(`${BASE_API_URL}/relationship/reward/romantic_moment/generate`, req)
  return res.data.data
}

// 一键已读
export const readAllMessages = async (
  types: ('system' | 'user' | 'engagement')[]
) => {
  const res = await axiosAuth.post<AppResp<ReadAllResponse>>(
    `${BASE_API_URL}/message/notification/read_all`,
    { types }
  )

  return res.data.data
}

// 获取未读消息状态
export const getUnreadMessageStatus = async () => {
  const res = await axiosAuth.post<AppResp<UnreadMessageStatus>>(
    `${BASE_API_URL}/message/notification/get_unread_status`,
    {
      platform: 'web',
    }
  )

  return res.data.data
}

export const getSystemMessageListKey =
  (req: Omit<SystemMessageListReq, 'page'>) => (page: number) => {
    return {
      endpoint: '/system_message_notification/sent_messages',
      req: { ...req, page },
    }
  }

// 获取系统消息列表
export const getSystemMessageList = async (req: SystemMessageListReq) => {
  const res = await axiosAuth.post<AppResp<SystemMessageRes>>(
    `${BASE_API_URL}/system_message_notification/sent_messages`,
    req
  )

  return res.data.data
}

// 获取系统消息详情
export const getSystemMessageDetail = async (message_id: string) => {
  const res = await axiosAuth.post<AppResp<SystemMessageDetail>>(
    `${BASE_API_URL}/system_message_notification/detail`,
    {
      message_id,
    }
  )

  return res.data.data
}

export const getUserMessageListKey =
  (req: Omit<SystemMessageListReq, 'page'>) => (page: number) => {
    return {
      endpoint: '/user_message_notification/sent_messages',
      req: { ...req, page },
    }
  }

// 获取用户消息列表
export const getUserMessageList = async (req: SystemMessageListReq) => {
  const res = await axiosAuth.post<AppResp<SystemMessageRes>>(
    `${BASE_API_URL}/user_message_notification/sent_messages`,
    req
  )

  return res.data.data
}

// 获取用户消息详情
export const getUserMessageDetail = async (message_id: string) => {
  const res = await axiosAuth.post<AppResp<SystemMessageDetail>>(
    `${BASE_API_URL}/user_message_notification/detail`,
    {
      message_id,
    }
  )

  return res.data.data
}

/**
 * 生成互动通知消息列表的SWR缓存key
 * 用于useSWRInfinite的key生成函数，支持分页数据获取
 * @param req - 请求参数（不包含page字段）
 * @returns 返回一个函数，该函数接收page参数并返回完整的SWR key对象
 */
export const getEngagementMessageListKey =
  (req: Omit<EngagementMessageListReq, 'page'>) => (page: number) => {
    return {
      endpoint: '/engagement_message_notification/sent_messages',
      req: {
        ...req,
        page,
      },
    }
  }

/**
 * 获取互动通知消息列表
 * 调用后端API获取用户的互动消息通知列表，支持分页
 * @param req - 请求参数，包含平台信息、分页参数等
 * @param req.platform - 平台类型 ('ios' | 'google_play' | 'apk')
 * @param req.page - 页码，从0开始
 * @param req.size - 每页数据量，默认20条
 * @returns Promise<EngagementMessageRes> 返回互动消息列表响应数据
 */
export const getEngagementMessageList = async (
  req: EngagementMessageListReq
) => {
  const res = await axiosAuth.post<AppResp<EngagementMessageRes>>(
    `${BASE_API_URL}/engagement_message_notification/sent_messages`,
    req
  )

  return res.data.data
}
